<template>
	<div>
		<!-- Displayed value -->
		<form-field-readonly
			@click="openModal"
			@remove:clicked="onUnselect"
			:hasRemove="computedValue.type ? true : false"
		>
			<div class="xs-12">
				<span v-if="computedValue.type" class="c1">
					{{ computedValue.type }}
				</span>
				<span v-if="computedValue.value" class=" c6 lh125"
					>({{ computedValue.value }})</span
				>
				<span v-else><slot name="empty">---</slot></span>
			</div>
		</form-field-readonly>

		<!-- Modal -->
		<core-modal :name="instanceKey" headline="Select Address">
			<div class="p4">
				<!-- Add new Address form -->
				<template v-if="showCreateForm">
					<entity-address-create
						:entityId="entityId"
						:entityName="entityName"
						:createOptions="createOptions"
						@address:created="onAddressCreated"
						@cancel="onToggleCreateAddress"
					/>
				</template>

				<template v-else>
					<!-- Entity name and crete new -->
					<div class="xs-12 flex wrap mb4">
						<!-- Headline -->
						<div class="xs-8 lh125">
							<span class="fw7">{{ entityName }}</span>
							{{ $t("has_these_addresses") }}:
						</div>
						<!-- Button -->
						<div class="flex xs-4 xs-right">
							<j-button type="tertiary" @click="onToggleCreateAddress">
								{{ $t("add_new_address") }}
							</j-button>
						</div>
					</div>

					<!-- Selection -->
					<div v-if="computedList && computedList.length">
						<div
							v-for="item in computedList"
							:key="item.id"
							class="r2 p2 mb2 cu2 bg5"
							:class="[
								{ 'bc2 b2': item.isSelected },
								{ 'bc7 b1': !item.isSelected }
							]"
							@click="onSelect(item.id)"
						>
							<!-- Type -->
							<div class="xs-12 flex wrap lh125 c1 fw7 mb2">
								<div class="xs-8">
									{{ item.type }}
								</div>
								<div class="flex xs-4 xs-right" v-if="item.isSuggested">
									<j-tag size="small" variant="warning" label="Suggested" />
								</div>
							</div>

							<!-- Iterate through fields2 -->
							<div v-if="item.fields2 && item.fields2.length">
								<div v-for="line in item.fields2" class="p2 bt1 bc7">
									<div class="xs-12 lh125 fs3 fw7 c6">
										{{ line.type }}
									</div>
									<div class="xs-12 lh125">
										{{ line.value }}
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- No data -->
					<div v-else class="xs-12 bg5 r2 b1 bc7 p4 ta2 fs3 c6 fw7 mb4">
						{{ $t("errors.no_data") }}
					</div>
				</template>
			</div>
		</core-modal>
	</div>
</template>

<script>
import EntityAddressCreate from "./EntityAddressCreate.vue"
import CoreModal from "@/modules/core/components/CoreModal"
import JButton from "@/components/JButton"
import FormFieldReadonly from "../FormFieldReadonly.vue"
import JTag from "@/components/JTag"
import { getFieldData, shouldSuggest } from "../utils/index.js"

export default {
	components: {
		EntityAddressCreate,
		CoreModal,
		JButton,
		FormFieldReadonly,
		JTag
	},

	props: {
		/**
		 * Value represents `id`
		 */
		value: {
			type: String
		},

		/**
		 * Field from schema
		 * It's an object that inlcudes numerous properties, read docs for more details
		 * We compute some values based on those properties
		 */
		field: {
			type: Object,
			default: () => ({})
		},

		/**
		 * Options for filtering suggested facts
		 */
		suggest: {
			type: Object
		},

		/**
		 * Instructions for creation of a new fact
		 * What properties it can included is in docs
		 */
		createOptions: {
			type: Object
		}
	},

	data() {
		return {
			instanceKey: Math.random().toString(), // get unique key fro modal window

			showCreateForm: false,

			// Form for adding new address
			isAdding: false,
			isAddingLoading: false,
			form: {
				type: "",
				value: ""
			}
		}
	},

	computed: {
		/**
		 * Entity object that we get from API
		 * We get some other properties from this object that are crucial for some operations
		 * e.g. entityId, entityName, entityType via __typename
		 * thus it has to contain properties: `__typename`, `id` `name`
		 */
		entity() {
			const entity = getFieldData("entity", this.field, this)
			return entity
		},

		/**
		 * Based on this we do API calls
		 */
		entityId() {
			return this.entity?.id
		},

		/**
		 * Based on this we do API calls
		 */
		entityType() {
			return this.entity?.__typename
		},

		/**
		 * When we need to display name of an entity
		 */
		entityName() {
			return this.entity?.name
		},

		/**
		 * Will return data based on relation field
		 */
		cache() {
			return getFieldData("relation", this.field, this)
		},

		/**
		 * What user can select from, displayed in UI
		 */
		computedList() {
			return this.cache
				?.map(item => ({
					...item,
					isSelected: item.id === this.value,
					isSuggested: shouldSuggest(item, this.suggest)
				}))
				.sort((a, b) => b.isSuggested - a.isSuggested) // sort based on suggested first
		},

		/**
		 * So that we do not display `id` but proper value
		 */
		computedValue() {
			const address = this?.cache?.find(item => item?.id === this.value)
			if (!address) return {}

			// Stringify address.fields2
			let stringifiedFields2 = address?.fields2
				?.map(item => item.value)
				.join("; ")

			return { type: address?.type, value: stringifiedFields2 }
		}
	},

	methods: {
		// When user wants to open a picker modal,
		// check if if we actually have `entity` to pick data for
		// If not, show message and don't show modal
		openModal() {
			if (!this.entity) {
				this.$store.dispatch("addMessage", {
					delay: 1500,
					message: {
						type: "neutral",
						text: "You have to select entity first"
					}
				})
				return
			}

			this.showCreateForm = false

			this.$modal.show(this.instanceKey)
		},

		onToggleCreateAddress() {
			this.showCreateForm = !this.showCreateForm
		},

		// When user selects an address
		onSelect(id) {
			this.$modal.hide(this.instanceKey)
			this.$emit("input", id)
		},

		// When user unselects
		onUnselect() {
			this.$emit("input", "")
			this.$modal.hide(this.instanceKey)
		},

		async onAddressCreated(id) {
			this.showCreateForm = false

			// Update all entities in store by refetching the case graph
			await this.$store.dispatch("caseManagement/fetchCase", {
				caseId: this.$store.state.caseManagement.caseId
			})

			// Select address automatically
			// Order is important, we do it after we update store
			this.onSelect(id)
		}
	}
}
</script>
